/**
 * Top-level orchestrator for the Auth Letter Upload modal.
 *
 * The modal opens on a single blank authorization tab — nothing is seeded from
 * upstream PA data. The form-state list is the canonical set of authorization
 * tabs; the sidebar renders it and the Candid payload is built from it.
 * Rendering is delegated to `AuthLetterUploadModalContent`.
 */
import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback } from "react";
import { ModalId } from "../../../enums/modalId";
import AuthLetterUploadModalContent from "./authLetterUploadModalContent";
import { AuthLetterUploadModalProps } from "./authLetterUploadModalTypes";
import { useAuthFormStates } from "./hooks/useAuthFormStates";
import { useAuthNavigation } from "./hooks/useAuthNavigation";
import { useAuthSaveFlow } from "./hooks/useAuthSaveFlow";
import { useCandidAuthorizationUpload } from "./hooks/useCandidAuthorizationUpload";

export type { AuthLetterUploadModalMetaData } from "./authLetterUploadModalTypes";

/**
 * Renders the Candid authorization trigger dialog.
 *
 * @param props.onClose - Clears Redux modal state so the dialog can reopen
 * @returns The modal element
 */
const AuthLetterUploadModal = ({ onClose }: AuthLetterUploadModalProps) => {
  const {
    selectedAuthorizationIndex,
    disabledIndexes,
    selectTab,
    deleteTab,
    addTab,
    advanceToNextTab,
  } = useAuthNavigation();

  const {
    formStates,
    currentFormState,
    handleFormFieldChange,
    addEmptyFormState,
    removeFormState,
  } = useAuthFormStates(selectedAuthorizationIndex);

  const { onUpload, onUploadSuccess } = useCandidAuthorizationUpload();
  const { isSaveDisabled, saveButtonText, handleSave, isUploading } =
    useAuthSaveFlow({
      formStates,
      selectedIndex: selectedAuthorizationIndex,
      advanceToNextTab,
      onUpload,
      onSuccess: onUploadSuccess,
    });

  /** Removes an authorization tab and re-indexes sidebar navigation. */
  const handleDeleteTab = useCallback(
    (index: number) => {
      removeFormState(index);
      deleteTab(index, formStates.length - 1);
    },
    [formStates.length, removeFormState, deleteTab],
  );

  /** Appends a blank authorization tab and selects it. */
  const handleAddMore = useCallback(() => {
    addEmptyFormState();
    addTab(formStates.length);
  }, [formStates.length, addEmptyFormState, addTab]);

  return (
    <Modal
      dialogId={ModalId.AUTH_LETTER_UPLOAD_MODAL}
      title="New Authorization"
      onClose={onClose}
      onSave={handleSave}
      onCancel={onClose}
      saveButtonText={isUploading ? "Uploading..." : saveButtonText}
      disableSave={isSaveDisabled}
      cancelText="Cancel"
    >
      <AuthLetterUploadModalContent
        formStates={formStates}
        selectedIndex={selectedAuthorizationIndex}
        disabledIndexes={disabledIndexes}
        currentFormState={currentFormState}
        onSelect={selectTab}
        onDelete={handleDeleteTab}
        onAddMore={handleAddMore}
        onFormChange={handleFormFieldChange}
        isSaveDisabled={isSaveDisabled}
      />
    </Modal>
  );
};

export default AuthLetterUploadModal;
